/**
 * sessionCriteriaFromTrack — S3 for a session, derived from its track.
 *
 * `createEvent` inherits the track's Criteria & Scales rows into the V3 event
 * on the server round-trip; the phone wizard has to show the same list *before*
 * it creates anything, so the rule lives here once instead of in both places
 * (reuse-before-build).
 *
 * Weighting: a track's criteria carry weights (`cycle_attribute_configs.weight`,
 * migration 1047). When every criterion on the track has one, the session starts
 * from them, scaled to whole points that add up to 100. When any has none, the
 * session starts from an even split, as before. Either way a session that wants a
 * different balance edits it (S3 `unset` or `inherited`-unlocked per
 * [sessionStepInheritance]). The iPhone mirrors this rule in
 * apps/ios/Priorities/SessionEngineCore.swift (inheritFromTrack).
 */

import type { CriterionDef } from '@/features/session-engine-v3/types';
import { apportion, normalizeTo100 } from './weightAllocation';

/** "value_at_risk" → "Value At Risk". Used when no attribute label exists. */
export function humanizeAttributeKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Build the session's criteria from a track's attribute keys, in track order.
 * Empty in → empty out: a track with no criteria is not an error, it means the
 * session sets its own (S3 `unset`).
 *
 * `weightFor` reads the track's weight for a key (null/undefined = none set).
 * Every key weighted, adding up to more than 0 → those weights scaled to 100;
 * otherwise the even split.
 */
export function criteriaFromTrackAttributeKeys(
  attributeKeys: readonly string[],
  labelFor?: (key: string) => string | undefined,
  weightFor?: (key: string) => number | null | undefined,
  valuesFromFor?: (key: string) => { from: CriterionDef['valuesFrom']; personId?: string | null } | null | undefined,
): CriterionDef[] {
  const keys = attributeKeys.filter((k) => typeof k === 'string' && k.trim().length > 0);
  if (keys.length === 0) return [];
  const trackWeights = trackWeightsFor(keys, weightFor);
  const equal = Math.floor(100 / keys.length) || 1;
  return keys.map((key, i) => {
    const def: CriterionDef = {
      id: key,
      name: labelFor?.(key)?.trim() || humanizeAttributeKey(key),
      weight: trackWeights ? trackWeights[positionKey(i)] : equal,
    };
    // Where the track says this criterion's values come from (1062) rides along,
    // so S4 can behave by it without asking the track again.
    const vf = valuesFromFor?.(key);
    if (vf?.from) {
      def.valuesFrom = vf.from;
      if (vf.from === 'specific_person' && vf.personId) def.valuesFromPersonId = vf.personId;
    }
    return def;
  });
}

/** The track's weights scaled to 100 (keyed by position), or null for the even split. */
function trackWeightsFor(
  keys: readonly string[],
  weightFor?: (key: string) => number | null | undefined,
): Record<string, number> | null {
  if (!weightFor) return null;
  const raw = keys.map((k) => weightFor(k));
  if (raw.some((w) => w == null || !Number.isFinite(Number(w)) || Number(w) < 0)) return null;
  const numbers = raw.map((w) => Number(w));
  if (numbers.reduce((s, w) => s + w, 0) <= 0) return null;
  return normalizeTo100(Object.fromEntries(numbers.map((w, i) => [positionKey(i), w])));
}

/**
 * Keys by position that sort in position order, so the allocator's last
 * tie-break (key order) is the earlier criterion, as on the server (1048) and
 * the iPhone (trackCriteriaWeights).
 */
function positionKey(i: number): string {
  return String(i).padStart(6, '0');
}

/**
 * A session's Event Designer criteria on its track's weights (migration 1048).
 * The room's S5 ranking computes from these (`v3Config.criteria`), so while the
 * track's criteria are locked they are the track's: the criteria the track has
 * take its weights as whole points; a criterion the track lacks keeps its weight
 * and the track's criteria share what it leaves of 100.
 *
 * `shares` is the track's weights as shares of 1 (trackWeightShares).
 */
// reuse-ack: mirrors internal_session_weights_on_track (v3Config part) in supabase/migrations/1048_a_session_on_a_track_scores_with_its_weights.sql — the server writes the saved copy, this one is the room's copy in memory
export function criteriaOnTrackWeights(
  criteria: readonly CriterionDef[],
  shares: Readonly<Record<string, number>>,
): CriterionDef[] {
  const onTrack = (c: CriterionDef) => Object.prototype.hasOwnProperty.call(shares, c.id);
  const own = criteria.filter((c) => !onTrack(c));
  if (own.length === criteria.length) return [...criteria];
  const ownTotal = own.reduce((s, c) => s + (Number.isFinite(c.weight) ? c.weight : 0), 0);
  let total = own.length === 0 ? 100 : Math.max(Math.round(100 - ownTotal), 0);
  if (total <= 0) total = 100;
  const points = apportion(
    criteria.flatMap((c, i) => (onTrack(c) ? [[positionKey(i), Math.max(0, shares[c.id])] as [string, number]] : [])),
    total,
  );
  return criteria.map((c, i) => (onTrack(c) ? { ...c, weight: points[positionKey(i)] } : c));
}

/**
 * Clamp weights to whole percentages that sum to 100, preserving relative
 * balance. A criteria list whose weights do not sum to 100 silently skews every
 * downstream score, so the wizard normalizes before it writes.
 */
export function normalizeCriteriaWeights(criteria: readonly CriterionDef[]): CriterionDef[] {
  if (criteria.length === 0) return [];
  // Keyed by position: two criteria may share an id while being edited.
  const shares = normalizeTo100(
    Object.fromEntries(criteria.map((c, i) => [String(i), Math.round(c.weight)])),
  );
  return criteria.map((c, i) => ({ ...c, weight: shares[String(i)] }));
}
